import { type ObjectConfig, type ObjectState } from "@homebound/form-state";
import { makeAutoObservable, observableRef } from "mobx";
import { type AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { hasPendingProposals } from "src/components/formFields/ai/utils/aiProposalForm";
import { type AiFormAepFragment } from "src/components/formFields/ai/utils/aiProposalForm.generated";
import { parseAiNumber } from "src/components/formFields/ai/utils/bindAiProposal";
import { AiEntityProposalType, AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { type PlanPackageLocationsV2_LocationFragment } from "../LocationsTab.generated";
import { type RoomFormValue, validateFullRoomNumber } from "./locationUtils";

type RoomSummary = { rplId: string; levelName: string; fullRoomNumber: number | null | undefined };

export type LocationRowInput = {
  rpl: PlanPackageLocationsV2_LocationFragment;
  levelName: string;
  proposal: AiFormAepFragment | undefined;
  fieldProposals: ObjectState<AiFieldProposalFormValue>[];
  rooms: RoomSummary[];
};

/** Writable room form backing with server context and the tab's live proposal states. */
export class LocationRow {
  id: string;
  roomTypeId: RoomFormValue["roomTypeId"];
  marketingName: RoomFormValue["marketingName"];
  roomNumber: RoomFormValue["roomNumber"];

  constructor(public input: LocationRowInput) {
    this.id = input.rpl.id;
    this.roomTypeId = input.rpl.roomType?.id;
    this.marketingName = input.rpl.marketingName;
    this.roomNumber = input.rpl.roomNumber;
    makeAutoObservable(this, { input: observableRef });
  }

  /** Refreshes read-only context; form-state reconciles writable fields and preserves local edits. */
  update(input: LocationRowInput): void {
    this.input = input;
  }

  get rpl() {
    return this.input.rpl;
  }

  get proposal() {
    return this.input.proposal;
  }

  get fieldProposals() {
    return this.input.fieldProposals;
  }

  get hasPendingProposals(): boolean {
    return hasPendingProposals(this.fieldProposals);
  }

  get isProposedForRemoval(): boolean {
    return this.proposal?.type.code === AiEntityProposalType.Delete;
  }

  /**
   * Whether resolving this room's proposals means deleting it.
   * Every defined status must be rejected for a CREATE or accepted for a DELETE, whether resolved
   * individually or by a whole-row action. Missing statuses are ignored; no statuses means no deletion.
   */
  get needsDelete(): boolean {
    const proposal = this.proposal;
    const statuses = this.fieldProposals.map((p) => p.status.value).compact();
    if (!proposal || statuses.isEmpty) return false;
    return (
      (proposal.type.code === AiEntityProposalType.Create &&
        statuses.every((s) => s === AiFieldProposalStatus.Rejected)) ||
      (proposal.type.code === AiEntityProposalType.Delete &&
        statuses.every((s) => s === AiFieldProposalStatus.Accepted))
    );
  }

  /**
   * Checks the live proposal against its level and the saved numbers of all rooms on the plan.
   * There is no warning column on `AiFieldProposal`, so these are computed the same way the Order cell
   * validates a number the user types.
   */
  get warning(): string | undefined {
    const proposal = this.fieldProposals.find((p) => p.fieldName.value === "roomNumber");
    // A resolved proposal has nothing left to warn about.
    if (proposal?.status.value !== AiFieldProposalStatus.Pending) return undefined;
    const full = parseAiNumber(proposal.fieldValue.value);
    if (full === undefined) {
      return "Missing room number — extracted without a number. Assign one to continue.";
    }
    const validated = validateFullRoomNumber(this.input.levelName, full);
    if ("error" in validated) {
      return `Unexpected numbering — ${validated.error}. "${full}" doesn't match the convention.`;
    }
    if (this.input.rooms.some((other) => other.rplId !== this.rpl.id && other.fullRoomNumber === full)) {
      return `Duplicate room number — ${full} is already used on this plan.`;
    }
    return undefined;
  }
}

/** The configured writable fields over the class instance retained by form-state. */
export type LocationRowForm = ObjectState<RoomFormValue> & { readonly value: LocationRow };

// Only writable room fields participate in refresh, dirty checks, and mutation payloads.
// Context is refreshed explicitly because class getters read input from the backing instance.
// Fragment fields remove their property from that instance; computed fields skip refresh.
export const locationRowConfig: ObjectConfig<RoomFormValue> = {
  id: { type: "value" },
  roomTypeId: { type: "value" },
  marketingName: { type: "value" },
  roomNumber: { type: "value" },
};
